import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

/**
 * Which permission a staff page needs — the browser half of
 * config/staff-access.php (sent in bootstrap data as `staff_access`).
 *
 * Presentation only: it decides which sidebar items show and whether a page
 * renders, so nobody lands on a screen whose API would refuse them. The API
 * enforces the same map on its own (EnforceStaffAccess).
 *
 * A path under a default-deny prefix (/admin, /dashboard) with no rule is
 * closed to everyone but super admins, so a page added without a rule stays
 * hidden instead of opening to all staff.
 */

type PageRule = string | string[];

interface StaffAccessData {
  pages: Record<string, PageRule>;
  default_deny: string[];
  dashboard_any: string[];
  admin_any?: string[];
}

function data(): StaffAccessData | null {
  return ((getBootstrapData() as any)?.staff_access as StaffAccessData) ?? null;
}

function normalise(path: string): string {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, '');
  return clean.startsWith('/') ? clean : `/${clean}`;
}

/** The most specific page rule for a path, or undefined when none matches. */
export function pageRuleFor(path: string): PageRule | undefined {
  const access = data();
  const map = access?.pages;
  if (!map) return undefined;
  const target = normalise(path);
  const segments = target.split('/').filter(Boolean);
  // A zone root ('/admin', '/dashboard') describes only the root page itself —
  // never everything below it, or an unmapped new page would inherit it and
  // open to every staff member instead of failing closed.
  const zoneRoots = new Set(access?.default_deny ?? []);

  let best: {rule: PageRule; length: number; wildcards: number} | undefined;
  for (const [pattern, rule] of Object.entries(map)) {
    const parts = pattern.split('/').filter(Boolean);
    if (zoneRoots.has(normalise(pattern)) && normalise(pattern) !== target) continue;
    if (parts.length > segments.length) continue;
    if (!parts.every((part, i) => part === '*' || part === segments[i])) continue;
    const wildcards = parts.filter(p => p === '*').length;
    if (
      !best ||
      parts.length > best.length ||
      (parts.length === best.length && wildcards < best.wildcards)
    ) {
      best = {rule, length: parts.length, wildcards};
    }
  }
  return best?.rule;
}

export function canOpenStaffPage(
  path: string | undefined | null,
  hasPermission: (name: string) => boolean,
): boolean {
  const access = data();
  if (!access || !path || /^[a-z]+:\/\//i.test(path)) return true;
  const target = normalise(path);
  const inZone = access.default_deny.some(
    prefix => target === prefix || target.startsWith(`${prefix}/`),
  );
  if (!inZone) return true;
  if (hasPermission('admin')) return true;

  const rule = pageRuleFor(target);
  if (rule === undefined || rule === 'super') return false;
  if (rule === 'public') return true;
  if (rule === 'dashboard-any') return access.dashboard_any.some(hasPermission);
  if (rule === 'admin-any') return (access.admin_any ?? []).some(hasPermission);
  return (Array.isArray(rule) ? rule : [rule]).some(hasPermission);
}
